'use client'

import { useLayoutEffect, useRef, useState } from 'react'

// Scaled, non-interactive thumbnail of a rendered editorial report. The full
// document is rendered in an isolated <iframe srcDoc> at a fixed logical width
// (PREVIEW_W, the report's max content width) and CSS-scaled down to the card
// width, so the masthead, stat band, and first table/chart all read at a glance
// — the same treatment on /free-competitor-analysis and /free-ads-audit. A
// bottom fade sells "there's more" without a hard crop.
//
// Shared by SampleReportArtifact (ads audit) and CompetitorSampleArtifact so the
// preview rendering stays identical in one place.

// PREVIEW_W matches the report's max content width so it fills edge-to-edge;
// PREVIEW_VISIBLE_H is how far down the document the card reveals before the
// fade.
const PREVIEW_W = 860
const PREVIEW_VISIBLE_H = 1180

export function ReportPreviewThumb({
  srcDoc,
  title,
}: {
  srcDoc: string
  title: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.5)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setScale(el.clientWidth / PREVIEW_W)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ height: PREVIEW_VISIBLE_H * scale }}
    >
      <iframe
        title={`${title} preview`}
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-same-origin"
        scrolling="no"
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none border-0"
        style={{
          width: PREVIEW_W,
          height: PREVIEW_VISIBLE_H + 600,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
      <div className="from-background pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t to-transparent" />
    </div>
  )
}
